"use client";

import { filterPublicCaseStudies } from "@/lib/publications/caseStudyVisibility";
import { CaseStudy } from "@/lib/publications/types";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

// --- Component Props ---
interface CaseStudyCarouselProps {
  variant: "carousel" | "continue-exploring";
  caseStudies?: CaseStudy[]; // Case studies data from API
  excludeSlug?: string; // For continue-exploring variant (deprecated - use excludeCreatedAt)
  excludeCreatedAt?: string; // For continue-exploring variant - exclude by creation date
  className?: string;
  title?: string; // Optional: custom section title
}

const CaseStudyCarousel: React.FC<CaseStudyCarouselProps> = ({
  variant,
  caseStudies = [],
  excludeSlug,
  excludeCreatedAt,
  className = "",
  title,
}) => {
  const publicCaseStudies = filterPublicCaseStudies(caseStudies);

  // Filter out current case study for continue-exploring variant - prefer createdAt over slug
  const caseStudiesToShow =
    excludeCreatedAt || excludeSlug
      ? publicCaseStudies.filter((cs) => {
          // Prefer filtering by createdAt (more reliable), fallback to slug for backward compatibility
          const isExcludedByCreatedAt = excludeCreatedAt && cs.createdAt === excludeCreatedAt;
          const isExcludedBySlug = !excludeCreatedAt && excludeSlug && cs.slug === excludeSlug;
          const isExcluded = isExcludedByCreatedAt || isExcludedBySlug;

          return !isExcluded;
        })
      : publicCaseStudies;

  // Early return if no data to show
  if (!caseStudiesToShow || caseStudiesToShow.length === 0) {
    return (
      <div className={`w-full p-8 text-center ${className}`}>
        <div className="text-gray-500">
          <p className="text-sm">No case studies available</p>
        </div>
      </div>
    );
  }

  // --- Carousel Variant (Landing style) ---
  if (variant === "carousel") {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    // Limit to 3 case studies for carousel
    const carouselCaseStudies = caseStudiesToShow.slice(0, 3);

    // Check scrollability
    const checkScroll = () => {
      const el = scrollRef.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    useEffect(() => {
      checkScroll();
      const el = scrollRef.current;
      if (!el) return;
      el.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
      return () => {
        el.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
      };
    }, [carouselCaseStudies]);

    const scrollBy = (dir: "left" | "right") => {
      const el = scrollRef.current;
      if (!el) return;
      // Scroll by a percentage of the container width for responsive behavior
      const scrollAmount = el.clientWidth * 0.8; // Scroll 80% of visible width
      el.scrollBy({ left: dir === "left" ? -scrollAmount : scrollAmount, behavior: "smooth" });
    };

    // Import Button here to avoid circular import issues
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { Button } = require("../ui/Button");

    return (
      <div
        className={`w-full flex justify-center items-center py-8 sm:py-12 md:py-16 lg:py-24 bg-landing-primary ${className}`}
      >
        <div className="w-full max-w-[1320px] mx-auto flex flex-col gap-8">
          {/* Section Header with Buttons */}
          <div className="w-full flex items-center justify-between ">
            <h2 className="text-h3 sm:text-h2 text-landing-primary pr-4">{title || "Explore Our Case Studies"}</h2>
            <div className="flex gap-2 flex-shrink-0">
              <button
                className={`bg-white border border-gray-200 rounded-full w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center transition-opacity ${
                  canScrollLeft ? "opacity-100" : "opacity-40 pointer-events-none"
                }`}
                onClick={() => scrollBy("left")}
                aria-label="Scroll left"
              >
                <ChevronLeft size={18} className="sm:w-[22px] sm:h-[22px]" />
              </button>
              <button
                className={`bg-white border border-gray-200 rounded-full w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center transition-opacity ${
                  canScrollRight ? "opacity-100" : "opacity-40 pointer-events-none"
                }`}
                onClick={() => scrollBy("right")}
                aria-label="Scroll right"
              >
                <ChevronRight size={18} className="sm:w-[22px] sm:h-[22px]" />
              </button>
            </div>
          </div>

          {/* Case Studies Carousel */}
          <div className="w-full">
            <div
              ref={scrollRef}
              className="flex gap-4 sm:gap-6 md:gap-8 overflow-x-auto hide-scrollbar scroll-smooth"
              style={{ scrollBehavior: "smooth" }}
            >
              {carouselCaseStudies.map((caseStudy) => (
                <div key={caseStudy.id} className="flex-shrink-0 transition-opacity w-80 md:w-[28rem] lg:w-[43.75rem]">
                  {/* Wrap entire card in clickable link */}
                  <a href={`/case-studies/${caseStudy.slug}`} className="block group">
                    {/* Large background card with hero image */}
                    <div className="w-full h-[300px] sm:h-[350px] md:h-[400px] lg:h-[450px] xl:h-[500px] rounded-xl sm:rounded-2xl lg:rounded-3xl bg-landing-secondary relative overflow-hidden">
                      {/* Hero Image Background */}
                      {caseStudy.heroImage ? (
                        <div className="w-full h-full relative">
                          {/* Background Hero Image - default blurred, hover clear */}
                          <img
                            src={caseStudy.heroImage.url}
                            alt={caseStudy.heroImage.alt}
                            className="object-cover w-full h-full blur-md group-hover:blur-none group-hover:scale-105 transition-all duration-300"
                          />

                          {/* Company logo - default 100% opacity, hover 0 opacity */}
                          {caseStudy.company.logo && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <img
                                src={caseStudy.company.logo.url}
                                alt={caseStudy.company.logo.alt}
                                className="object-contain max-h-16 max-w-32 mx-auto opacity-100 group-hover:opacity-0 transition-opacity duration-300"
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Fallback: Company Logo centered with dark background */
                        <div className="flex items-center justify-center h-full p-4 sm:p-6 md:p-8 bg-gradient-to-b from-gray-800 to-gray-900 relative">
                          {/* Dark overlay for consistency */}
                          <div className="absolute inset-0 transition-all duration-300" />
                          {caseStudy.company.logo && (
                            <img
                              src={caseStudy.company.logo.url}
                              alt={caseStudy.company.logo.alt}
                              className="object-contain max-h-16 max-w-32 mx-auto relative z-10"
                            />
                          )}
                        </div>
                      )}
                    </div>

                    {/* Testimonial content - responsive design */}
                    <div className="mt-4 sm:mt-6 md:mt-8 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 lg:gap-6">
                      <div className="flex-1 max-w-full">
                        {caseStudy.testimonial && (
                          <>
                            <blockquote
                              className="text-body-lg text-landing-primary mb-2 sm:mb-3 overflow-hidden"
                              style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }}
                            >
                              "{caseStudy.testimonial.quote}"
                            </blockquote>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-left">
                              <span className="text-base sm:text-lg font-semibold text-landing-primary">
                                {caseStudy.testimonial.author.name}
                              </span>
                              {caseStudy.testimonial.author.title && (
                                <span className="text-base sm:text-lg text-landing-secondary">
                                  {caseStudy.testimonial.author.title}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      <div className="lg:ml-8 flex-shrink-0">
                        <Button
                          variant="secondary"
                          color="primary"
                          size="sm"
                          className="w-auto text-sm"
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            e.stopPropagation();
                            window.open(`/case-studies/${caseStudy.slug}`, "_blank");
                          }}
                        >
                          Read story
                        </Button>
                      </div>
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Continue Exploring Variant ---
  if (variant === "continue-exploring") {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    // Check scrollability
    const checkScroll = () => {
      const el = scrollRef.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    useEffect(() => {
      checkScroll();
      const el = scrollRef.current;
      if (!el) return;
      el.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
      return () => {
        el.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
      };
    }, [caseStudiesToShow]);

    const scrollBy = (dir: "left" | "right") => {
      const el = scrollRef.current;
      if (!el) return;
      const card = el.querySelector(".case-card");
      const cardWidth = card ? (card as HTMLElement).offsetWidth : 320;
      el.scrollBy({ left: dir === "left" ? -cardWidth * 2 : cardWidth * 2, behavior: "smooth" });
    };

    return (
      <div
        className={`w-full max-w-[1320px] mx-auto flex flex-col items-start py-8 sm:py-12 md:py-16 relative ${className}`}
      >
        {/* Title and Arrows Row */}
        <div className="w-full flex items-center justify-between mb-6 sm:mb-8 px-4 sm:px-6 md:px-8 lg:px-0">
          <div className="text-h4 sm:text-h3 font-bold text-left pr-4">{title || "Continue exploring"}</div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              className={`bg-white border border-gray-200 rounded-full w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center transition-opacity ${
                canScrollLeft ? "opacity-100" : "opacity-40 pointer-events-none"
              }`}
              onClick={() => scrollBy("left")}
              aria-label="Scroll left"
            >
              <ChevronLeft size={18} className="sm:w-[22px] sm:h-[22px]" />
            </button>
            <button
              className={`bg-white border border-gray-200 rounded-full w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center transition-opacity ${
                canScrollRight ? "opacity-100" : "opacity-40 pointer-events-none"
              }`}
              onClick={() => scrollBy("right")}
              aria-label="Scroll right"
            >
              <ChevronRight size={18} className="sm:w-[22px] sm:h-[22px]" />
            </button>
          </div>
        </div>

        {/* Cards Row */}
        <div className="relative w-full">
          <div
            ref={scrollRef}
            className="flex gap-4 sm:gap-6 overflow-x-auto hide-scrollbar px-4 sm:px-6 md:px-8 lg:px-0 scroll-smooth"
            style={{ scrollBehavior: "smooth" }}
          >
            {caseStudiesToShow.map((cs) => (
              <a key={cs.id} href={`/case-studies/${cs.slug}`} className="flex flex-col items-start group">
                <div className="case-card flex-shrink-0 w-56 sm:w-64 md:w-72 rounded-xl sm:rounded-2xl border border-gray-200 flex flex-col items-center justify-between p-4 sm:p-6 md:p-8 mb-2 transition group-hover:shadow-md min-w-[200px] max-w-[320px] relative overflow-hidden">
                  {/* Hero Image Background */}
                  {cs.heroImage ? (
                    <div className="absolute inset-0 w-full h-full">
                      {/* Background Hero Image - default blurred, hover clear */}
                      <img
                        src={cs.heroImage.url}
                        alt={cs.heroImage.alt}
                        className="object-cover w-full h-full blur-sm group-hover:blur-none group-hover:scale-105 transition-all duration-300"
                      />
                    </div>
                  ) : (
                    /* Fallback: Gray background */
                    <div className="absolute inset-0 bg-gray-100" />
                  )}

                  <div className="flex items-center justify-center h-16 sm:h-20 md:h-24 w-full mb-4 sm:mb-6 relative z-10">
                    <img
                      src={cs.company.logo.url}
                      alt={cs.company.logo.alt}
                      className="object-contain max-h-16 max-w-32 p-2 mx-auto mt-2 opacity-100 group-hover:opacity-0 transition-opacity duration-300"
                    />
                  </div>
                </div>
                <div
                  className="text-sm sm:text-body text-left text-gray-800 ml-2 w-full max-w-[200px] sm:max-w-[256px] md:max-w-[320px] overflow-hidden"
                  style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                >
                  {cs.previewTitle}
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CaseStudyCarousel;
